import React, { useState } from 'react';
import { Building2, TrendingUp, TrendingDown, DollarSign, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const StorePerformanceCard = ({ data }) => {
  const [selectedStore, setSelectedStore] = useState(null);
  const [view, setView] = useState('best');
  
  const sortedStores = data
    .sort((a, b) => view === 'best' 
      ? a.maintenanceCost - b.maintenanceCost 
      : b.maintenanceCost - a.maintenanceCost)
    .slice(0, 8);

  const StoreCard = ({ store, index }) => {
    const isPositive = view === 'best';
    const trendValue = Math.round(Math.random() * 20);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        onClick={() => setSelectedStore(store)}
        className="group relative"
      >
        <div className={`absolute inset-0 transition-all duration-300 group-hover:bg-gray-50`} />
        <div className="relative p-4 flex items-center gap-4">
          {/* Rank */}
          <div className={`relative flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
            index === 2 ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white' :
            'bg-gray-100 text-gray-600'
          }`}>
            <span className="text-sm font-bold">{index + 1}</span>
            {index < 3 && (
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center">
                {index === 0 ? '🏆' : index === 1 ? '🥈' : '🥉'}
              </div>
            )}
          </div>

          {/* Store Info */}
          <div className="flex-1 flex items-center justify-between min-w-0">
            <div className="truncate">
              <h4 className="font-medium text-gray-900 flex items-center gap-2 truncate">
                {store.name}
                {index === 0 && (
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    view === 'best' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {view === 'best' ? 'Most Efficient' : 'Critical'}
                  </span>
                )}
              </h4>
              <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {store.ticketCount}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {store.responseTime}h
                </span>
              </div>
            </div>

            {/* Cost & Trend */}
            <div className="text-right ml-4">
              <div className="text-sm font-semibold text-gray-900">
                ${store.maintenanceCost.toLocaleString()}
              </div>
              <div className={`flex items-center gap-0.5 text-xs ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? 
                  <TrendingDown className="w-3.5 h-3.5" /> : 
                  <TrendingUp className="w-3.5 h-3.5" />}
                {trendValue}% {isPositive ? 'lower' : 'higher'}
              </div>
            </div>
          </div>

          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
        </div>
      </motion.div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Store Performance</h3>
          <div className="flex items-center gap-2 bg-gray-100/80 p-1 rounded-lg">
            <button
              onClick={() => setView('best')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                view === 'best' 
                  ? 'bg-white text-green-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Best Performing
            </button>
            <button
              onClick={() => setView('worst')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                view === 'worst' 
                  ? 'bg-white text-red-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Needs Attention
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-gray-50/80">
            <div className="text-xs text-gray-500">Average Cost</div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              ${Math.round(data.reduce((acc, store) => acc + store.maintenanceCost, 0) / data.length).toLocaleString()}
            </div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50/80">
            <div className="text-xs text-gray-500">Total Issues</div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {data.reduce((acc, store) => acc + store.ticketCount, 0)}
            </div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50/80">
            <div className="text-xs text-gray-500">Avg Response</div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {(data.reduce((acc, store) => acc + store.responseTime, 0) / data.length).toFixed(1)}h
            </div>
          </div>
        </div>
      </div>

      {/* Store List */}
      <div className="divide-y divide-gray-100">
        {sortedStores.map((store, index) => (
          <StoreCard key={store.name} store={store} index={index} />
        ))}
      </div>

      {/* Store Details Modal */}
      <AnimatePresence>
        {selectedStore && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setSelectedStore(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-lg w-full mx-4"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal content here */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StorePerformanceCard; 